import type { Driver } from '@cobliteam/api-lib';
import { Divider, Typography } from '@cobliteam/ui/components';
import { Form, useForm } from '@cobliteam/ui/form';
import { formatPhoneNumber } from '@cobliteam/utils/phoneNumber';
import { zodResolver } from '@hookform/resolvers/zod';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import DriverAppAccess from './components/DriverAppAccess';
import DriverCPFInput from './components/DriverCPFInput';
import DriverIdentifierInput from './components/DriverIdentifierInput';
import DriverLicenseDateInput from './components/DriverLicenseDateInput';
import DriverLicenseInput from './components/DriverLicenseInput';
import DriverNameInput from './components/DriverNameInput';
import DriverPhoneInput from './components/DriverPhoneInput';
import DriverRegistryInput from './components/DriverRegistryInput';
import { formatCNH, formatCPF } from './components/formatters';
import { ReplaceTokenAssociationConfirmModal } from './components/ReplaceTokenAssociationConfirmModal';
import type { FormValues } from './validationSchema';
import validationSchema from './validationSchema';

export interface DriverFormRef {
  handleSubmit: () => void;
  isDirty: () => boolean;
}

interface DriverFormProps {
  driver: Driver;
  allDriversData: Driver[];
  isLoading?: boolean;
  setCanSubmit: (value: boolean) => void;
  onFinish: (driver: Driver, hasChanges: boolean) => void;
}

const formatDriver = (driver: Driver) => ({
  ...driver,
  phoneNumbers: driver.phoneNumbers.map(formatPhoneNumber),
  cpf: driver.cpf ? formatCPF(driver.cpf) : undefined,
  driverLicense: driver.driverLicense ? formatCNH(driver.driverLicense) : undefined,
});

const DriverForm = forwardRef<DriverFormRef, DriverFormProps>(({ driver, allDriversData, isLoading = false, setCanSubmit, onFinish }: DriverFormProps, ref) => {
  const [openReplaceTokenAssociationConfirmDialog, setOpenReplaceTokenAssociationConfirmDialog] = useState(false);
  const [associatedDriverName, setAssociatedDriverName] = useState<string>();
  const [associatedTokenAlias, setAssociatedTokenAlias] = useState<string>();

  const schema = validationSchema(driver, allDriversData);

  const driverForm = useForm<FormValues>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: formatDriver(driver),
  });

  const { getValues, formState } = driverForm;

  useEffect(() => {
    setCanSubmit(formState.isValid);
  }, [setCanSubmit, formState]);

  // Reset form only when editing a different driver (driver.id changes), not on data refetches
  // that return the same driver with a new object reference. Including the full `driver` object
  // would reset the form on every background refetch, discarding in-progress edits.
  useEffect(() => {
    driverForm.reset(formatDriver(driver));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver.id, driverForm.reset]);

  const submitDriver = async () => {
    const formValues = getValues();
    const parsedDriver = schema.parse(formValues);

    const stagedDriver = {
      id: driver.id,
      fleetId: driver.fleetId,
      identifier: driver.identifier,
      ...parsedDriver,
    } as Driver;

    setOpenReplaceTokenAssociationConfirmDialog(false);
    setAssociatedDriverName(undefined);
    onFinish(stagedDriver, driverForm.formState.isDirty);
  };

  const onSubmit = async (formValues: FormValues) => {
    const token = formValues.identificationToken;

    if (token?.tokenAlias) {
      const changedIdentificationToken = token?.tokenAlias !== driver.identificationToken?.tokenAlias;

      const driverAssociated = allDriversData.find((a) => a.id === token?.driverId);

      if (token?.driverId !== undefined && driverAssociated?.name !== driver.name && changedIdentificationToken) {
        setAssociatedDriverName(driverAssociated?.name);
        setAssociatedTokenAlias(token.isExternal ? token.tokenAlias : token.tokenAlias?.toUpperCase());
        setOpenReplaceTokenAssociationConfirmDialog(true);
      } else {
        return submitDriver();
      }
    } else {
      return submitDriver();
    }
  };

  const handleSubmit = driverForm.handleSubmit(onSubmit);

  useImperativeHandle(ref, () => {
    return {
      handleSubmit,
      isDirty: () => driverForm.formState.isDirty,
    };
  }, [handleSubmit]);

  const replaceCancel = () => {
    setOpenReplaceTokenAssociationConfirmDialog(false);
    setAssociatedDriverName(undefined);
  };

  return (
    <>
      <Form form={driverForm} onSubmit={onSubmit}>
        <div id='addDriverDrawer' className='h-fit w-full'>
          <DriverNameInput />
          <div className='flex gap-5'>
            <DriverPhoneInput />
            <DriverAppAccess />
          </div>
          <Divider />
          <DriverIdentifierInput allDriversData={allDriversData} form={driverForm} />
          <Divider />
          <Typography variant='text-lg-semibold' color='secondary' className='my-4'>
            Documentos do motorista
          </Typography>

          <div className='flex flex-col'>
            <div className='flex gap-3'>
              <DriverRegistryInput />
              <DriverCPFInput />
            </div>
            <div>
              <DriverLicenseInput />
              <DriverLicenseDateInput />
            </div>
          </div>
        </div>
      </Form>
      <ReplaceTokenAssociationConfirmModal
        open={openReplaceTokenAssociationConfirmDialog}
        loading={isLoading}
        currentAssociatedDriver={associatedDriverName}
        token={associatedTokenAlias || ''}
        onCancel={replaceCancel}
        onConfirm={submitDriver}
      />
    </>
  );
});

export default DriverForm;
